import { useEffect, useState } from 'react';
import { AppState, Dimensions, Platform, useWindowDimensions } from 'react-native';
import { getNavigationMode, type NavigationModeInfo } from 'react-native-navigation-mode';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export function useBottomInsetSmart() {
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const [navigationMode, setNavigationMode] = useState<NavigationModeInfo | null>(null);

  const isAndroid = Platform.OS === 'android';
  const isAndroid13OrBelow = isAndroid && Number(Platform.Version) <= 33;
  const shouldUseNavigationMode = isAndroid && !isAndroid13OrBelow;

  // The native module reads `activity.window.decorView.rootWindowInsets`. After a
  // Samsung DeX / Desktop Mode toggle the decorView needs time to re-layout before
  // it reports the new navigation bar insets. We aggressively refetch on multiple
  // signals so a stale 0 from either source can't persist:
  //   - dimension / safe-area change (deps)
  //   - a staircase of timeouts to catch slow settles
  //   - a direct Dimensions listener (fires earlier than `useWindowDimensions` on some devices)
  //   - AppState becoming active (covers DeX detach which can pause/resume the activity)
  useEffect(() => {
    if (!shouldUseNavigationMode) return;
    let mounted = true;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const fetchMode = () => {
      getNavigationMode()
        .then(mode => {
          if (mounted) setNavigationMode(mode);
        })
        .catch(() => {});
    };

    const scheduleRetries = () => {
      timeouts.forEach(clearTimeout);
      timeouts.length = 0;
      [50, 200, 500, 1000, 2000].forEach(delay => {
        timeouts.push(setTimeout(fetchMode, delay));
      });
    };

    fetchMode();
    scheduleRetries();

    const dimSub = Dimensions.addEventListener('change', () => {
      fetchMode();
      scheduleRetries();
    });

    const appStateSub = AppState.addEventListener('change', state => {
      if (state === 'active') {
        fetchMode();
        scheduleRetries();
      }
    });

    return () => {
      mounted = false;
      timeouts.forEach(clearTimeout);
      dimSub.remove();
      appStateSub.remove();
    };
  }, [shouldUseNavigationMode, width, height, insets]);

  if (!shouldUseNavigationMode) {
    return insets.bottom;
  }

  // Take the larger of the two so a stale value from either source can't undershoot.
  // After a Desktop -> Phone switch, `insets.bottom` becomes correct first; after a
  // Phone -> Desktop switch, both should converge to 0 once the retry settles.
  const navHeight = navigationMode?.navigationBarHeight ?? 0;
  return Math.max(insets.bottom, navHeight);
}
